'use client'

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Button, Form, FormInstance, Input, Modal, Radio } from 'antd'
import {
  CodeOutlined,
  LockOutlined,
  MailOutlined,
  NumberOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useSignIn, useSignUp } from '@/api'
import { emitter, EVENT_SHOW_SIGN_IN_MODAL } from '@/events'
import { useUserStore } from '@/feature/user/store'

export interface SignInOrSignUpProps {}

export type SignInOrSignUpRef = {
  open: () => void
}

interface FormValues {
  username: string
  password: string
}

const passwordValidator = (_: any, value: string, callback: () => void) => {
  if ((value?.length || 0) < 6) {
    return Promise.reject('密码长度不能小于6位')
  }

  callback()
  return Promise.resolve()
}

const commonFormItems = (
  <>
    <Form.Item
      name="username"
      rules={[{ required: true, message: '请输入用户名' }]}
    >
      <Input
        prefix={<UserOutlined className="mr-1" />}
        placeholder="用户名"
        maxLength={255}
      />
    </Form.Item>

    <Form.Item
      name="password"
      rules={[
        {
          validator: passwordValidator,
        },
      ]}
    >
      <Input
        prefix={<LockOutlined className="mr-1" />}
        type="password"
        placeholder="密码"
      />
    </Form.Item>
  </>
)

const SignIn = ({
  form,
  onClickSignUp,
  onSignUpSuccess,
}: {
  form: FormInstance
  onClickSignUp: () => void
  onSignUpSuccess: () => void
}) => {
  const signInMutation = useSignIn()
  const userStore = useUserStore()

  return (
    <Form
      form={form}
      onFinish={(values) => {
        signInMutation.mutateAsync(values).then((res) => {
          userStore.setUser({ token: res.token })
          onSignUpSuccess()
        })
      }}
    >
      {commonFormItems}

      <div>
        <Button block type="primary" htmlType="submit">
          登录
        </Button>

        <div className="mt-4 flex items-center justify-center">
          <span>还没有账号？</span>
          <Button type="link" onClick={onClickSignUp}>
            现在注册
          </Button>
        </div>
      </div>
    </Form>
  )
}

interface SignUpProps {
  onClickSignIn: () => void
  onSignUpSuccess: (formValues: FormValues) => void
}

const SignUp = ({ onClickSignIn, onSignUpSuccess }: SignUpProps) => {
  const [form] = Form.useForm()

  const signUpMutation = useSignUp()

  return (
    <Form<FormValues & { email: string }>
      onFinish={(values) => {
        signUpMutation.mutateAsync(values).then(() => {
          form.resetFields()
          onSignUpSuccess(values)
        })
      }}
    >
      <Form.Item
        name="email"
        rules={[
          {
            validator: (_, value) => {
              const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              const result = regex.test(value)

              if (!result) {
                return Promise.reject('请输入正确的邮箱')
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input prefix={<MailOutlined className="mr-1" />} placeholder="邮箱" />
      </Form.Item>

      <Form.Item
        name="phone_number"
        rules={[
          {
            validator(rule, value) {
              if (!/^1[3456789]\d{9}$/.test(value)) {
                return Promise.reject('请输入正确的手机号')
              }

              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          prefix={<PhoneOutlined className="mr-1" />}
          placeholder="手机号"
          maxLength={255}
        />
      </Form.Item>

      {commonFormItems}

      <Form.Item
        name="confirmPassword"
        rules={[
          {
            validator: async (rule, value, callback) => {
              const password = form.getFieldValue('password')

              if (password && password !== value) {
                return Promise.reject('两次输入的密码不一致')
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          prefix={<LockOutlined className="mr-1" />}
          type="password"
          placeholder="确认密码"
        />
      </Form.Item>

      <Form.Item
        name="invite_code"
        rules={[{ required: true, message: '请输入邀请码' }]}
      >
        <Input
          prefix={<NumberOutlined className="mr-1" />}
          placeholder="邀请码"
        />
      </Form.Item>

      <div>
        <Button
          block
          type="primary"
          htmlType="submit"
          loading={signUpMutation.isLoading}
        >
          注册
        </Button>

        <div className="mt-4 flex justify-center">
          <Button
            type="link"
            onClick={onClickSignIn}
            disabled={signUpMutation.isLoading}
          >
            返回登录
          </Button>
        </div>
      </div>
    </Form>
  )
}

const SignInOrSignUp = forwardRef<SignInOrSignUpRef, SignInOrSignUpProps>(
  (props, ref) => {
    const {} = props
    const [open, setOpen] = useState(false)
    const [isSignIn, setIsSignIn] = useState(true)

    useImperativeHandle(ref, () => ({
      open: () => {
        setOpen(true)
      },
    }))

    const handleOpenModal = useCallback(() => {
      setOpen(true)
    }, [])

    useEffect(() => {
      emitter.on(EVENT_SHOW_SIGN_IN_MODAL, handleOpenModal)
      return () => {
        emitter.off(EVENT_SHOW_SIGN_IN_MODAL, handleOpenModal)
      }
    }, [handleOpenModal])

    const [signInForm] = Form.useForm()

    return (
      <Modal
        title={<div className="text-center">{isSignIn ? '登录' : '注册'}</div>}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={400}
      >
        <div className="mt-4 p-4">
          {isSignIn ? (
            <SignIn
              form={signInForm}
              onClickSignUp={() => {
                setIsSignIn(false)
              }}
              onSignUpSuccess={() => {
                setOpen(false)
              }}
            />
          ) : (
            <SignUp
              onClickSignIn={() => {
                setIsSignIn(true)
              }}
              onSignUpSuccess={(values) => {
                signInForm.setFieldsValue(values)
                setIsSignIn(true)
              }}
            />
          )}
        </div>
      </Modal>
    )
  },
)

SignInOrSignUp.displayName = 'SignInOrSignUp'

export default SignInOrSignUp
